// Aceptación pública de la cotización sobre TanStack Query. Rutas públicas: no
// dependen de la sesión de `useAuth`, igual que `useInvitacion`.

import { useMutation, useQuery } from "@tanstack/react-query";

import type { Comuna } from "@/lib/comunas";
import { apiFetch } from "@/lib/api";
import {
  aceptarConEnlaceRequest,
  fetchCotizacionParaAceptar,
  type PayloadDeAceptacion,
} from "@/lib/aceptacionPublica";

export function useCotizacionParaAceptar(token: string | undefined) {
  return useQuery({
    queryKey: ["aceptacion-publica", token] as const,
    queryFn: () => fetchCotizacionParaAceptar(token!),
    enabled: Boolean(token),
    // 404/410/409 son estados VÁLIDOS del enlace —no existe, ya se usó, la
    // cotización se reemplazó— y cada uno tiene su propia pantalla. Reintentar
    // los repetiría sin cambiar nada y demoraría el mensaje que el cliente
    // necesita leer.
    retry: false,
  });
}

export function useAceptarConEnlace(token: string | undefined) {
  return useMutation({
    mutationFn: (payload: PayloadDeAceptacion) => aceptarConEnlaceRequest(token!, payload),
  });
}

/**
 * Las comunas para el selector de la facturación, por la puerta del enlace de
 * aceptación (la ruta interna vive tras una sesión que el cliente no tiene).
 *
 * Se piden SOLO cuando el cliente contesta que sí requiere factura: quien
 * responde que no no tiene por qué bajarse 25 KB que no va a mirar.
 */
export function useComunasParaAceptar(token: string | undefined, habilitado: boolean) {
  return useQuery({
    queryKey: ["aceptacion-comunas", token] as const,
    queryFn: async () => {
      const respuesta = await apiFetch<{ data: Comuna[] }>(
        `/api/aceptacion/${encodeURIComponent(token!)}/comunas`,
      );
      return respuesta.data;
    },
    enabled: Boolean(token) && habilitado,
    staleTime: Infinity,
    retry: false,
  });
}
